import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import {
  getGifts,
  requestThemedReading,
  type ReadingRecord,
  type ThemedTheme,
} from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import TarotCard from '@/components/TarotCard';
import RegenerateButton from '@/components/RegenerateButton';
import { useAudio } from '@/hooks/useAudio';
import { usePayment } from '@/hooks/usePayment';
import { useSubscription } from '@/hooks/useSubscription';

const THEMES: { key: ThemedTheme; icon: string }[] = [
  { key: 'love',      icon: '❤️' },
  { key: 'work',      icon: '💼' },
  { key: 'money',     icon: '💰' },
  { key: 'health',    icon: '🌱' },
  { key: 'spiritual', icon: '🌟' },
];

/**
 * Themed three-card reading. User picks one of five life spheres + an
 * optional personal question, gets a structured reading with fixed
 * positions: where you are / what blocks you / where to go.
 */
export default function Themed() {
  const { t } = useTranslation();
  const audio = useAudio();

  const [theme, setTheme] = useState<ThemedTheme | null>(null);
  const [question, setQuestion] = useState('');
  const [reading, setReading] = useState<ReadingRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGift, setHasGift] = useState(false);

  const { pay, paying, payError } = usePayment('themed_reading');
  const { isPriestess } = useSubscription();

  useEffect(() => {
    getGifts()
      .then((g) => setHasGift(g.products.includes('themed_reading')))
      .catch(() => {});
  }, []);

  const runReading = async () => {
    if (!theme) return;
    setError(null);
    try {
      const r = await requestThemedReading({
        theme,
        question: question.trim() || undefined,
      });
      audio.play('shuffle');
      setReading(r);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onSubmit = () => {
    if (!theme || busy) return;
    if (isPriestess || hasGift) {
      setBusy(true);
      runReading().finally(() => setBusy(false));
      return;
    }
    pay(runReading);
  };

  const reset = () => {
    setReading(null);
    setError(null);
  };

  const cards = (reading?.cards ?? []) as any[];
  const positionLabels = [
    t('themed.pos_now'),
    t('themed.pos_block'),
    t('themed.pos_forward'),
  ];

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        🎯 {t('themed.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('themed.subtitle')}
      </p>

      <AnimatePresence mode="wait">
        {!reading ? (
          <motion.section
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            {/* Theme picker */}
            <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/60">
              {t('themed.pick_theme')}
            </p>
            <div className="mb-4 grid grid-cols-1 gap-2">
              {THEMES.map((th) => {
                const selected = theme === th.key;
                return (
                  <button
                    key={th.key}
                    type="button"
                    onClick={() => setTheme(th.key)}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5
                                text-left transition
                                ${selected
                                  ? 'border-gold-500 bg-gold-500/20 text-gold-100'
                                  : 'border-gold-500/25 text-gold-300/70 hover:border-gold-400/60'}`}
                  >
                    <span className="text-xl">{th.icon}</span>
                    <span>
                      <span className="block font-serif text-base">
                        {t(`themed.theme_${th.key}`)}
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-gold-500/60">
                        {t(`themed.theme_${th.key}_blurb`)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Optional question */}
            {theme && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
              >
                <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('themed.question_label')}
                </p>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder={t('themed.question_placeholder')}
                  rows={3}
                  maxLength={200}
                  className="w-full resize-none rounded-lg border border-gold-500/30
                             bg-night-900/60 px-3 py-2 font-serif text-base
                             text-gold-100 outline-none transition
                             placeholder:text-gold-300/35 focus:border-gold-400"
                />
                <p className="mt-1 text-right text-[10px] text-gold-300/40">
                  {question.length}/200
                </p>
              </motion.div>
            )}

            <button
              type="button"
              onClick={onSubmit}
              disabled={!theme || busy || paying}
              className="btn-primary w-full disabled:opacity-40"
            >
              {paying || busy
                ? t('payment.opening')
                : isPriestess
                  ? t('themed.open_priestess')
                  : hasGift
                    ? t('themed.open_gift')
                    : t('themed.open_for_stars', { stars: 25 })}
            </button>
            {isPriestess && hasGift && (
              <div className="mt-2 space-y-0.5 text-center text-[10px] uppercase tracking-widest">
                <p className="text-gold-400/70">✦ {t('spread.included_priestess')}</p>
                <p className="text-emerald-300/80">🎁 {t('spread.gift_kept_for_later')}</p>
              </div>
            )}
            {isPriestess && !hasGift && (
              <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-gold-400/70">
                ✦ {t('spread.included_priestess')}
              </p>
            )}
            {!isPriestess && hasGift && (
              <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-emerald-300/80">
                🎁 {t('spread.gift_explain')}
              </p>
            )}
            {(payError || error) && (
              <p className="mt-3 text-center text-xs text-rose-400">
                {payError ?? error}
              </p>
            )}

            <p className="mt-5 text-center text-xs italic text-gold-300/50">
              {t('themed.description')}
            </p>
          </motion.section>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* 3 cards in a row */}
            <div className="flex justify-center gap-3">
              {cards.map((c, i) => (
                <motion.div
                  key={i}
                  initial={{ rotateY: 180, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  transition={{ delay: i * 0.2, duration: 0.7 }}
                  className="flex flex-col items-center text-center"
                >
                  <TarotCard card={c} size={100} />
                  <span className="mt-1.5 text-[10px] uppercase tracking-widest text-gold-500/60">
                    {i + 1}
                  </span>
                  <span className="text-[11px] leading-snug text-gold-300/80">
                    {positionLabels[i]}
                  </span>
                </motion.div>
              ))}
            </div>

            <OrnateFrame className="card-glow">
              <RichText
                text={reading.interpretation}
                className="font-serif italic leading-relaxed text-gold-200"
              />
            </OrnateFrame>

            {reading.id && (
              <RegenerateButton
                readingId={reading.id}
                onUpdated={setReading}
                className="mt-5"
              />
            )}

            <div className="mt-4 flex items-center justify-between gap-3">
              <button type="button" onClick={reset} className="nav-back-pill">
                <span>←</span>
                <span>{t('themed.new_reading')}</span>
              </button>
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={t('reading.scroll_to_top')}
                className="nav-scroll-top"
              >
                ↑
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
